import { convex } from "@/lib/convexClient";
import { api } from "../../convex/_generated/api";
import { isElectron } from "@/services/storage/LocalFileSystemClient";

type ProjectLike = {
  data?: {
    faviconUrl?: string;
    inheritedCraftspaceFavicon?: string;
  };
  craftspace_id?: string;
};

/**
 * Gets the craftspace favicon URL for a given craftspace ID
 * @param craftspaceId - The craftspace ID to get the favicon for
 * @returns Promise<string | null> - The craftspace favicon URL or null if not found
 */
export const getCraftspaceFavicon = async (craftspaceId: string): Promise<string | null> => {
  if (isElectron()) return null;
  try {
    const craftspace = await convex.query(api.craftspaces.get, { id: craftspaceId });

    if (!craftspace) {
      return null;
    }

    return craftspace.icon_url || null;
  } catch (error) {
    console.warn("Error getting craftspace favicon:", error);
    return null;
  }
};

/**
 * Gets the effective favicon URL for a project, considering both custom project favicon and craftspace favicon
 * @param projectData - The project data object
 * @param craftspaceFavicon - The craftspace favicon URL (optional, will be fetched if not provided)
 * @returns Promise<string | null> - The effective favicon URL
 */
export const getProjectEffectiveFavicon = async (
  projectData: ProjectLike,
  craftspaceFavicon?: string | null,
): Promise<string | null> => {
  // First check if project has a custom favicon
  const projectFavicon = projectData?.data?.faviconUrl;
  if (projectFavicon) {
    return projectFavicon;
  }

  // If no craftspace favicon provided, fetch it
  if (craftspaceFavicon === undefined && projectData?.craftspace_id) {
    craftspaceFavicon = await getCraftspaceFavicon(projectData.craftspace_id);
  }

  return craftspaceFavicon || null;
};

/**
 * Checks if a project is using craftspace favicon (no custom favicon set)
 * @param projectData - The project data object
 * @returns boolean - True if using craftspace favicon, false if using custom favicon
 */
export const isUsingCraftspaceFavicon = (projectData: ProjectLike): boolean => {
  return !projectData?.data?.faviconUrl;
};

/**
 * Gets the effective favicon URL for display purposes
 * @param projectData - The project data object
 * @param craftspaceFavicon - The craftspace favicon URL (optional)
 * @returns string | null - The effective favicon URL to display
 */
export const getEffectiveFaviconUrl = (
  projectData: ProjectLike,
  craftspaceFavicon?: string | null,
): string | null => {
  // First check if project has a custom favicon
  const projectFavicon = projectData?.data?.faviconUrl;
  if (projectFavicon) {
    return projectFavicon;
  }

  // Fall back to craftspace favicon
  return craftspaceFavicon || projectData?.data?.inheritedCraftspaceFavicon || null;
};
